import { api } from './client';

export interface SalesInvoiceItem {
  id: number;
  itemCode: string | null;
  itemName: string;
  hsn: string | null;
  unit: string;
  qty: string;
  rate: string;
  discountAmt: string;
  taxableAmt: string;
  gstPct: string;
  cgst: string;
  sgst: string;
  igst: string;
  lineTotal: string;
}

export interface SalesInvoice {
  id: number;
  invoiceNo: string;
  invoiceDate: string;
  dueDate: string | null;
  doId: number | null;
  outwardGrnId: number | null;
  partyId: number;
  partyGstin: string | null;
  partyStateCode: string | null;
  isInterstate: boolean;
  discount: string | null;
  specialDiscount: string | null;
  totalDiscount: string;
  taxableTotal: string;
  totalCgst: string;
  totalSgst: string;
  totalIgst: string;
  totalGst: string;
  freightCharges: string;
  grandTotal: string;
  paymentTerms: string | null;
  irn: string | null;
  ewayBillNo: string | null;
  transporterName: string | null;
  vehicleNo: string | null;
  lrNo: string | null;
  termsOfDelivery: string | null;
  bankDetails: string | null;
  status: 'Draft' | 'Issued' | 'Partial' | 'Paid' | 'Cancelled';
  partialNote: string | null;
  items: SalesInvoiceItem[];
  party?: { id: number; name: string; gstin: string | null; customerType: string | null; mobile: string | null; whatsapp: string | null };
  dispatchOrder?: { id: number; doNo: string };
  outwardGrn?: { id: number; grnNo: string };
}

export async function listInvoices(filter: {
  status?: string;
  partyId?: number;
  from?: string;
  to?: string;
  search?: string;
} = {}): Promise<SalesInvoice[]> {
  const r = await api.get<SalesInvoice[]>('/sales-invoices', { params: filter });
  return r.data;
}

export async function getInvoice(id: number): Promise<SalesInvoice> {
  const r = await api.get<SalesInvoice>(`/sales-invoices/${id}`);
  return r.data;
}

export async function createInvoiceFromGrn(payload: {
  outwardGrnId: number;
  invoiceDate?: string;
  freightCharges?: number;
  paymentTerms?: string;
  irn?: string;
  ewayBillNo?: string;
  lrNo?: string;
  termsOfDelivery?: string;
  bankDetails?: string;
}): Promise<SalesInvoice> {
  const r = await api.post<SalesInvoice>('/sales-invoices/from-grn', payload);
  return r.data;
}

export async function updateInvoice(
  id: number,
  payload: {
    paymentTerms?: string;
    dueDate?: string;
    vehicleNo?: string;
    lrNo?: string;
    /** Draft only: each line's new rate and discount; GST and totals are recalculated. */
    lines?: { id: number; rate: number; discountAmt: number }[];
    irn?: string;
    ewayBillNo?: string;
    bankDetails?: string;
    partialNote?: string;
    /** Why the invoice is being changed — kept in Invoice Logs. */
    reason: string;
  },
): Promise<SalesInvoice> {
  const r = await api.patch<SalesInvoice>(`/sales-invoices/${id}`, payload);
  return r.data;
}

export async function issueInvoice(id: number): Promise<SalesInvoice> {
  const r = await api.post<SalesInvoice>(`/sales-invoices/${id}/issue`);
  return r.data;
}

export async function cancelInvoice(id: number, reason: string): Promise<SalesInvoice> {
  const r = await api.post<SalesInvoice>(`/sales-invoices/${id}/cancel`, { reason });
  return r.data;
}

export async function markInvoicePaid(
  id: number,
  payload: { amount: number; mode: string; ref?: string },
): Promise<SalesInvoice> {
  const r = await api.post<SalesInvoice>(`/sales-invoices/${id}/mark-paid`, payload);
  return r.data;
}

/** One change to an invoice, as kept in Invoice Logs. */
export interface InvoiceLogChange { field: string; label: string; from: string; to: string }
export interface InvoiceLog {
  id: number; invoiceId: number; invoiceNo: string; action: string; changes: InvoiceLogChange[];
  reason: string | null; actorName: string | null; actorRole: string | null; ip: string | null; at: string;
}
export async function getInvoiceLogs(filter: { search?: string; action?: string; user?: string; from?: string; to?: string }): Promise<InvoiceLog[]> {
  const r = await api.get<InvoiceLog[]>('/sales-invoices/logs', { params: filter });
  return r.data;
}

/** A file attached to an invoice (signed copy, proof of delivery, customer PO…). */
export interface InvoiceAttachment {
  id: number; invoiceId: number; kind: string; fileName: string; url: string; mime: string | null;
  sizeBytes: number; note: string | null; uploadedByName: string | null; at: string;
}
export const ATTACHMENT_KINDS = ['Signed copy', 'Proof of delivery', 'Customer PO', 'Other'];
export async function getInvoiceAttachments(id: number): Promise<InvoiceAttachment[]> {
  const r = await api.get<InvoiceAttachment[]>(`/sales-invoices/${id}/attachments`);
  return r.data;
}
export async function addInvoiceAttachment(id: number, file: File, kind: string, note?: string): Promise<InvoiceAttachment> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('kind', kind);
  if (note?.trim()) fd.append('note', note.trim());
  const r = await api.post<InvoiceAttachment>(`/sales-invoices/${id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return r.data;
}
export async function removeInvoiceAttachment(id: number, attachmentId: number, reason: string): Promise<void> {
  await api.delete(`/sales-invoices/${id}/attachments/${attachmentId}`, { data: { reason } });
}
